import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectRegulatedPower, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import RegulatedBreakers from "./RegulatedBreakers"; // Change component import
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

const RegulatedPower = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "regulatedPower"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectRegulatedPower);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  // const loading = storedJobDetails.length === 0;

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;
  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        comments: jobDetails.Comments || "",
        supplyType: jobDetails.SupplyType || "",
        quantity: jobDetails.Quantity || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  /****************Add dropdown options here*************************/

  const supplyTypeOptions =
    options?.filter(
      (option) => option.DropdownType === "Supply Type"
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mechanical-responsive-grid electrical-power-responsive">
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="supplyType">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SupplyType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Supply Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="supplyType"
                  value={formData.supplyType || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Supply Type",
                      supplyTypeOptions,
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "SupplyType",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.supplyType} // Apply dynamic class
                  onChange={handleChange}
                >
                  <option value="">Select Supply Type</option>
                  {supplyTypeOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.supplyType,
                    supplyTypeOptions
                  ) &&
                    formData.supplyType && (
                      <option
                        value={formData.supplyType}
                        style={{ color: "red" }}
                      >
                        *{formData.supplyType}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group controlId="quantity">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Quantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="quantity"
                  value={formData.quantity || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Quantity", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "Quantity",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.quantity} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Quantity"
                />
              </Form.Group>
            </Col>
          </Row>
          <h4 className="subsection-header mb-3">
               Regulated Breakers
              </h4>

          <Row className="mb-3 mechanical-form-full-width">
            <RegulatedBreakers zone={zone} />
          </Row>

          <Row className="mb-3 mechanical-form-full-width">
            <Col>
              <Form.Group controlId="comments">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comments"
                  value={formData.comments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Comments", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "Comments",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.comments} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
          </div>

          <div></div>
        </div>
      )}
    </div>
  );
};

export default RegulatedPower;
